// Constants dùng chung cho module Warranty Service / Phiếu sửa chữa (FE).
// BE mirror: ddv-dcore-api/src/utils/enum.js → WARRANTY_SLIP_STATUS + state-machine.js.

// Single source of truth cho slip status (key). Mirror BE WARRANTY_SLIP_STATUS.
export const SLIP_STATUS = Object.freeze({
    RECEIVING: 'receiving',
    CHECKING: 'checking',
    REPAIRING: 'repairing',
    WAITING_PART: 'waiting_part',
    SENT_TO_VENDOR: 'sent_to_vendor',
    COMPLETED: 'completed',
    NOTIFIED: 'notified',
    RETURNED: 'returned',
    REJECTED_QUOTE: 'rejected_quote',
    BER: 'ber',
    CANCELLED: 'cancelled',
});

// Label + variant cho hiển thị badge. Sửa label/màu chỉ ở đây.
export const SLIP_STATUS_META = Object.freeze({
    [SLIP_STATUS.RECEIVING]: { text: 'Đang tiếp nhận', variant: 'info' },
    [SLIP_STATUS.CHECKING]: { text: 'Đang kiểm tra', variant: 'primary' },
    [SLIP_STATUS.REPAIRING]: { text: 'Đang sửa chữa', variant: 'primary' },
    [SLIP_STATUS.WAITING_PART]: { text: 'Chờ linh kiện', variant: 'warning' },
    [SLIP_STATUS.SENT_TO_VENDOR]: { text: 'Gửi hãng', variant: 'warning' },
    [SLIP_STATUS.COMPLETED]: { text: 'Hoàn thành', variant: 'success' },
    [SLIP_STATUS.NOTIFIED]: { text: 'Đã thông báo KH', variant: 'success' },
    [SLIP_STATUS.RETURNED]: { text: 'Đã trả máy', variant: 'success' },
    [SLIP_STATUS.REJECTED_QUOTE]: { text: 'KH từ chối báo giá', variant: 'secondary' },
    [SLIP_STATUS.BER]: { text: 'BER', variant: 'dark' },
    [SLIP_STATUS.CANCELLED]: { text: 'Đã huỷ', variant: 'danger' },
});

// Dropdown / table — derived từ META, giữ thứ tự khai báo trong SLIP_STATUS.
export const SLIP_STATUS_OPTIONS = Object.values(SLIP_STATUS).map((value) => ({
    value,
    text: SLIP_STATUS_META[value].text,
    variant: SLIP_STATUS_META[value].variant,
}));

// State machine — mirror BE src.v2/core-modules/repair-slip/state-machine.js.
export const SLIP_STATE_TRANSITIONS = Object.freeze({
    [SLIP_STATUS.RECEIVING]: [SLIP_STATUS.CHECKING, SLIP_STATUS.CANCELLED],
    [SLIP_STATUS.CHECKING]: [
        SLIP_STATUS.REPAIRING,
        SLIP_STATUS.COMPLETED,
        SLIP_STATUS.WAITING_PART,
        SLIP_STATUS.SENT_TO_VENDOR,
        SLIP_STATUS.REJECTED_QUOTE,
        SLIP_STATUS.BER,
        SLIP_STATUS.CANCELLED,
    ],
    [SLIP_STATUS.REPAIRING]: [
        SLIP_STATUS.COMPLETED,
        SLIP_STATUS.WAITING_PART,
        SLIP_STATUS.SENT_TO_VENDOR,
        SLIP_STATUS.CANCELLED,
    ],
    [SLIP_STATUS.WAITING_PART]: [SLIP_STATUS.REPAIRING, SLIP_STATUS.CANCELLED],
    [SLIP_STATUS.SENT_TO_VENDOR]: [SLIP_STATUS.REPAIRING, SLIP_STATUS.CANCELLED],
    [SLIP_STATUS.COMPLETED]: [SLIP_STATUS.NOTIFIED, SLIP_STATUS.REPAIRING, SLIP_STATUS.CANCELLED],
    [SLIP_STATUS.NOTIFIED]: [SLIP_STATUS.RETURNED],
    [SLIP_STATUS.REJECTED_QUOTE]: [SLIP_STATUS.NOTIFIED, SLIP_STATUS.RETURNED],
    [SLIP_STATUS.BER]: [SLIP_STATUS.NOTIFIED, SLIP_STATUS.RETURNED],
    [SLIP_STATUS.RETURNED]: [],
    [SLIP_STATUS.CANCELLED]: [],
});

export const SLIP_STATUSES_REQUIRE_NOTE = [
    SLIP_STATUS.CANCELLED,
    SLIP_STATUS.BER,
    SLIP_STATUS.REJECTED_QUOTE,
];

export const SLIP_STATUSES_REQUIRE_VENDOR = [SLIP_STATUS.SENT_TO_VENDOR];

export const SLIP_STATUSES_REQUIRE_CANCEL_REASON = [SLIP_STATUS.CANCELLED];

// Trạng thái cho phép nhập Phí chẩn đoán (BER / rejected_quote).
export const SLIP_TERMINAL_CHARGE_STATUSES = [SLIP_STATUS.BER, SLIP_STATUS.REJECTED_QUOTE];

export const CANCEL_REASON_OPTIONS = [
    { value: null, text: '— Chọn lý do —' },
    { value: 'customer_changed_mind', text: 'KH đổi ý' },
    { value: 'cost_too_high', text: 'Phí quá cao' },
    { value: 'duplicate', text: 'Phiếu trùng / nhập sai' },
    { value: 'other', text: 'Lý do khác (ghi chú bên dưới)' },
];

export const ORDER_SOURCE_OPTIONS = [
    { value: '', text: '— Chọn nguồn —' },
    { value: 'cr_hotline', text: 'Khách CR Hotline' },
    { value: 'cr_crm', text: 'Khách CR CRM' },
    { value: 'old', text: 'Khách cũ' },
    { value: 'ddv', text: 'Khách DDV' },
    { value: 'cr_mess', text: 'Khách CR Mess' },
    { value: 'online', text: 'Khách Online (FB, Zalo, TikTok, IG)' },
    { value: 'vdd', text: 'Khách VDD' },
    { value: 'walkin', text: 'Khách vãng lai' },
    { value: 'web', text: 'Khách Web' },
    { value: 'referral', text: 'Khách được bạn giới thiệu' },
    { value: 'mou', text: 'Khách MOU' },
    { value: 'flyer', text: 'Khách tờ rơi' },
    { value: 'internal', text: 'Khách nội bộ' },
];

// Loại action ghi nhận trong RepairSlipHistory — mirror BE REPAIR_HISTORY_ACTION enum.
export const HISTORY_ACTION = Object.freeze({
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    STATUS_CHANGE: 'status_change',
    ADD_PART: 'add_part',
    ADD_SERVICE: 'add_service',
    REFUND: 'refund',
    UNDO: 'undo',
    TRANSFER: 'transfer',
});

export const HISTORY_ACTION_META = Object.freeze({
    [HISTORY_ACTION.CREATE]: { text: 'Tạo phiếu', variant: 'info' },
    [HISTORY_ACTION.UPDATE]: { text: 'Cập nhật', variant: 'primary' },
    [HISTORY_ACTION.DELETE]: { text: 'Xoá', variant: 'danger' },
    [HISTORY_ACTION.STATUS_CHANGE]: { text: 'Đổi trạng thái', variant: 'success' },
    [HISTORY_ACTION.ADD_PART]: { text: 'Thêm linh kiện', variant: 'warning' },
    [HISTORY_ACTION.ADD_SERVICE]: { text: 'Thêm dịch vụ', variant: 'warning' },
    [HISTORY_ACTION.REFUND]: { text: 'Hoàn tiền', variant: 'danger' },
    [HISTORY_ACTION.UNDO]: { text: 'Hoàn tác', variant: 'secondary' },
    [HISTORY_ACTION.TRANSFER]: { text: 'Luân chuyển', variant: 'info' },
});

// Trạng thái cho phép thêm/sửa/xoá linh kiện trên phiếu (Decision 1.29: stop sau completed).
export const SLIP_STATUSES_PART_EDITABLE = Object.freeze([
    SLIP_STATUS.RECEIVING,
    SLIP_STATUS.CHECKING,
    SLIP_STATUS.REPAIRING,
    SLIP_STATUS.WAITING_PART,
]);

// Lý do hoàn tiền — mirror BE refund reasons (REQ-F6.3).
export const REFUND_REASON_OPTIONS = Object.freeze([
    { value: null, text: '— Chọn lý do —' },
    { value: 'wrong_charge', text: 'Tính phí sai' },
    { value: 'service_not_done', text: 'Không sửa được — đã thu phí' },
    { value: 'customer_complaint', text: 'KH khiếu nại — đồng ý hoàn' },
    { value: 'partial_refund', text: 'Hoàn 1 phần (linh kiện không dùng)' },
    { value: 'other', text: 'Khác (ghi chi tiết bên dưới)' },
]);

// PRODUCT_TYPE — mirror BE src/utils/enum.js PRODUCT_TYPE (subset dùng trong warranty-service).
// 1=Product có kho (linh kiện), 2=Máy IMEI, 3=Combo, 4=SP dịch vụ (không kho), 5=SP bảo hành.
export const PRODUCT_TYPE = Object.freeze({
    PRODUCT: 1,
    PRODUCT_IMEI: 2,
    PRODUCT_COMBO: 3,
    PRODUCT_SERVICE: 4,
    PRODUCT_WARRANTY: 5,
});

// Mã loại trả phiếu khi → COMPLETED (mirror BE seed RepairReturnTypes.code).
// return_ok = thu phí/bảo hành, return_out = không sửa được trả máy, return_free = miễn phí.
export const RETURN_TYPE_CODE = Object.freeze({
    RETURN_OK: 'return_ok',
    RETURN_OUT: 'return_out',
    RETURN_FREE: 'return_free',
});

// Window cho phép hoàn tác trạng thái "Đã trả máy" (Decision #9: 24h từ returnedAt).
export const UNDO_RETURN_WINDOW_MS = 24 * 60 * 60 * 1000;

// Tối thiểu 5 ký tự cho lý do hoàn tác (audit log REQ-F9.2).
export const UNDO_RETURN_MIN_NOTE_LEN = 5;

// ============================================================
// SRS t101348 — Luân chuyển phiếu sửa chữa liên cửa hàng (mirror BE WARRANTY_TRANSFER_STATUS)
// ============================================================
export const TRANSFER_STATUS = Object.freeze({
    PENDING: 1,
    SHIPPING: 2,
    RECEIVED: 3,
    REJECTED: 4,
});

export const TRANSFER_STATUS_META = Object.freeze({
    [TRANSFER_STATUS.PENDING]: { text: 'Chờ vận chuyển', variant: 'secondary' },
    [TRANSFER_STATUS.SHIPPING]: { text: 'Đang vận chuyển', variant: 'warning' },
    [TRANSFER_STATUS.RECEIVED]: { text: 'Đã nhận', variant: 'success' },
    [TRANSFER_STATUS.REJECTED]: { text: 'Đã huỷ', variant: 'danger' },
});

export const TRANSFER_DIRECTION = Object.freeze({
    OUTBOUND: 'outbound', // A→B (đi sửa)
    RETURN: 'return', // B→A (trả về gốc)
});

// Trạng thái phiếu cho phép tạo lệnh chuyển ĐI (A→B) — mirror BE TRANSFER_OUT_ALLOWED_FROM (D2).
export const TRANSFER_OUT_ALLOWED_FROM = [SLIP_STATUS.RECEIVING, SLIP_STATUS.CHECKING];

// Trạng thái cho phép tạo lệnh TRẢ về gốc — completed (bình thường) + waiting_part/sent_to_vendor
// (trả sớm, bắt buộc nhập lý do — D5).
export const TRANSFER_RETURN_NORMAL_FROM = [SLIP_STATUS.COMPLETED];
export const TRANSFER_RETURN_EARLY_FROM = [SLIP_STATUS.WAITING_PART, SLIP_STATUS.SENT_TO_VENDOR];

// BR-02 — mirror BE state-machine.ORIGIN_ONLY_STATUSES. Các trạng thái CHỈ cửa hàng gốc
// được đặt khi máy đã về (notified/returned: thao tác khách; cancelled/ber/rejected_quote:
// terminal — đặt khi máy còn ở CH sửa sẽ kẹt máy vì không còn đường trả về). Ẩn khỏi
// dropdown đổi trạng thái khi máy chưa ở CH gốc.
export const ORIGIN_ONLY_STATUSES = [
    SLIP_STATUS.NOTIFIED,
    SLIP_STATUS.RETURNED,
    SLIP_STATUS.CANCELLED,
    SLIP_STATUS.BER,
    SLIP_STATUS.REJECTED_QUOTE,
];
